import { determineColor } from '../../util/color'
import { money, number } from '../../util/formatters'
import { MarketplaceI } from '../../util/marketplaces'
import { basisPointsToPercentage, decimalToBasisPoints, percentageToDecimal } from './AcosCellCalcs'

export interface RoasCellProps {
    roas: number
    acos: number
    target_acos?: number
    default_target_acos: number
    cost: number
    marketplace?: MarketplaceI
    version: number
}

export const calculate = ({
    roas,
    acos: acosInput,
    target_acos,
    default_target_acos,
    cost,
    marketplace,
    version = 1,
}: RoasCellProps) => {
    let target = target_acos || default_target_acos
    let acos = acosInput

    if (version === 1) {
        target = basisPointsToPercentage(target)
        acos = percentageToDecimal(acos)
    } else if (version === 2) {
        target = decimalToBasisPoints(target)
    } else {
        throw new Error(`Invalid version: ${version}`)
    }

    const color = determineColor(acos * 100, target, cost)
    const sales = cost / acos
    const m = marketplace ?? null

    return {
        spend: money(cost, m) ?? '',
        sales: money(sales, m) ?? '',
        roas: number(roas, 2) ?? '',
        color,
    }
}

/**
 * Callers (see `BlendedROAS` in table2/ad_grid_columns.tsx) pass `acos` = the row's
 * **blended_acos**, not the plain ACOS. That is what makes the tooltip's "Blended
 * Profit" line correct without ever reading the `blended_profit` field:
 *
 *   Laravel computes  blended_acos = cost / blended_profit * 100   (a percentage)
 *   so, inverting it, blended_profit = cost / blended_acos * 100
 *
 * Both sides are in dollars here — `money()` formats its input as-is (it does NOT
 * divide by 100), and the API already returns `cost` in dollars via
 * `ProductsController::COST_QUERY` (`sum(cost)/100.0`). The `* 100` below is
 * therefore undoing the percentage in blended_acos, not a unit conversion.
 *
 * Net effect: this line is *derived from* blended_profit (via blended_acos), so it
 * always agrees with the Blended Profit column. Worked example:
 *   cost $555.72, blended_acos 862.79%  ->  555.72 / 862.79 * 100 = $64.41
 *
 * `blendedRoas` is read straight off the row; it is not recomputed here.
 */
export const calculateBlended = ({
    roas,
    acos,
    target_acos,
    default_target_acos,
    cost,
    marketplace,
}: RoasCellProps) => {
    // target_acos/default_target_acos are basis points; blended acos is a percentage.
    const target = (target_acos || default_target_acos) / 100
    const color = determineColor(acos, target, cost)
    // Recovers blended_profit (dollars) by inverting blended_acos. See the note above.
    const blendedProfit = (cost / acos) * 100
    const m = marketplace ?? null

    return {
        blendedProfit: money(blendedProfit, m) ?? '',
        spend: money(cost, m) ?? '',
        blendedRoas: number(roas, 2) ?? '',
        color,
    }
}
